import React, { useState, useEffect, useRef } from "react";
import { Constants } from "../../../constants/appConstants";
import { OrdersUpdate } from "../../../models/csos/ordersUpdate";
import { UpdateQuantity } from "../../../models/csos/updateQuantity";
import { csosApiService } from "../../../services/api/csosApiService";
import { userService } from "../../../services/userService";
import { useOrderDetailsService } from "../../../services/component/orderDetailsService";
import { buildModifiedOrderLines } from "../../../utils/buildModifiedOrderLines";
import { fetchAndProcessOrder } from "../../../utils/fetchAndProcessOrder";
import ModalContentCenter from "../../modalContentCenter/ModalContentCenter";

interface SignApprovedOrderProps {
  order: any;
  setOrder: (order: any) => void;
}

const SignApprovedOrder: React.FC<SignApprovedOrderProps> = ({ order, setOrder }) => {
  const orderDetailsService = useOrderDetailsService();
  const [loggedInUser] = useState(userService.getCurrentUser());
  const [showApproveSignIn, setShowApproveSignIn] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showErrorDTK, setShowErrorDTK] = useState(false);
  const [errorDTKNonOrders, setErrorDTKNonOrders] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const isPrimary = (order.status === Constants.APPROVED && loggedInUser === order.updatedBy) ||
    (order.status === Constants.FAILURE && order.axwayFailed);

  const signCsosOrders = () => {
    approveQuantity(Constants.Operation_Name_APPROVE);
  };

  const openSignPopUp = () => {
    setShowApproveSignIn(true);
    setTimeout(() => {
      if (passwordInputRef.current) {
        passwordInputRef.current.value = "";
        passwordInputRef.current.focus();
      }
    }, 0);
  };

  const signOrder = (axwayPassword: string) => {
    const ordersUpdate = new OrdersUpdate();
    ordersUpdate.operationName = "ORDER_SIGNIN";
    ordersUpdate.axwayPassword = axwayPassword;
    ordersUpdate.statusCode = Constants.APPROVED;
    ordersUpdate.orders = [{
      customerNumber: order.customerNum,
      distributionCenter: order.dcNum,
      orderCreateSTP: "",
      orderId: order.orderId,
      uniqueTrackingNumber: "",
      uniqueTrackingNumberOld: "",
    }];
    csosApiService.updateCsosOrders(ordersUpdate).then((response) => {
      if (response.body && response.body.hostMessage === "SUCCESS") {
        orderDetailsService.resetTimer("reset");
        orderDetailsService.announceDetailClose("close");
        setShowApproveSignIn(false);
        orderDetailsService.refreshorder("refresh");
      } else if (response.body && response.body.hostMessage === "None approved orders for sign in") {
        setErrorDTKNonOrders(true);
      } else {
        setShowApproveSignIn(false);
        setShowError(true);
      }
    }).catch(() => {
      setShowError(true);
    });
  };

  const approveQuantity = (status: string) => {
    const orderQuantity = new UpdateQuantity();
    orderQuantity.orderId = order.orderId;
    orderQuantity.distributionCenter = order.dcNum;
    orderQuantity.customerNumber = order.customerNum;
    orderQuantity.orderCreateSTP = order.submitted;
    orderQuantity.operationName = status;
    orderQuantity.statusCode = order.status;
    orderQuantity.orderLines = buildModifiedOrderLines(order.orderLines);

    if (orderQuantity.orderLines.length !== 0) {
      csosApiService.updateQuantity(orderQuantity).then((response) => {
        if (response.body && response.body.hostMessage === "SUCCESS") {
          openSignPopUp();
        } else {
          setShowErrorDTK(true);
        }
      }).catch(() => {
        setShowError(true);
        refreshCurrentOrder();
      });
    } else {
      const ordersUpdate = new OrdersUpdate();
      ordersUpdate.operationName = status;
      ordersUpdate.orders = [{
        customerNumber: order.customerNum,
        distributionCenter: order.dcNum,
        orderCreateSTP: "",
        orderId: order.orderId,
        uniqueTrackingNumber: "",
        uniqueTrackingNumberOld: "",
      }];
      ordersUpdate.statusCode = order.status;
      csosApiService.updateCsosOrders(ordersUpdate).then((response) => {
        if (response.body && response.body.hostMessage === "SUCCESS") {
          openSignPopUp();
        } else {
          setShowErrorDTK(true);
          refreshCurrentOrder();
        }
      }).catch(() => {
        setShowError(true);
        refreshCurrentOrder();
      });
    }
  };

  const refreshCurrentOrder = () => {
    setErrorDTKNonOrders(false);
    setShowErrorDTK(false);
    setShowError(false);
    const account = order.dcNum + "-" + order.customerNum;
    fetchAndProcessOrder(order.orderId, account).then((o) => {
      if (o) {
        setOrder({ ...order, ...o });
      }
    }).catch(() => {
      setShowError(true);
    });
  };

  const closeFailedLayer = () => {
    setShowError(false);
    orderDetailsService.announceDetailClose("close");
    orderDetailsService.refreshorder("refresh");
    orderDetailsService.resetTimer("reset");
  };

  const closeOverridenPopup = () => {
    orderDetailsService.announceDetailClose("close");
    orderDetailsService.refreshorder("refresh");
    orderDetailsService.resetTimer("reset");
    setShowError(false);
  };

  return (
    <>
      <button type="button"
        className={`btn btn-default nonprimary ${isPrimary ? "primaryaction" : ""}`}
        onClick={signCsosOrders}>Approve &amp; Sign</button>

      {/* Sign Password Modal */}
      <ModalContentCenter visible={showApproveSignIn}>
        <div className="modal-fade center" role="dialog">
          <div className="modal-dialog signYourOrderModal">
            <div className="modal-content modal-layout-sign-pop-up">
              <div className="footer-modal-header">
                <button type="button" className="close" onClick={() => setShowApproveSignIn(false)}>&times;</button>
                <h4 className="footer-modal-title">Sign Your Order</h4>
              </div>
              <div className="footer-modal-body">
                <div className="sign-note">To sign your order, enter the password you created when registering your DEA certificate.</div>
                <div className="form-group sign-password-box">
                  <label className="signing-text" htmlFor="signingOrderPwd">Signing Password:</label>
                  <div className="sign-password">
                    <input type="password" className="form-control" ref={passwordInputRef} id="signingOrderPwd"
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                      onKeyUp={(e) => { if (e.key === "Enter") signOrder((e.target as HTMLInputElement).value); }} />
                  </div>
                </div>
                <p>If necessary, click Forgot Password below to register your certificate again and create a new password. Please contact the Customer Service Center at 800-326-6457 if you require additional support.</p>
              </div>
              <div className="modal-footer sign-order-footer">
                <a className="forgotpasswd" target="_blank" rel="noopener noreferrer" href={Constants.REGISTER_CERTIFICATE_URL}>Forgot Password</a>
                <button type="button" className="btn btn-default cancelbtn nonprimary" onClick={() => setShowApproveSignIn(false)}>Cancel</button>
                <button type="button" className="btn btn-default signorder" onClick={() => signOrder(passwordInputRef.current?.value || "")}>Sign Order</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* Error - Not signed */}
      <ModalContentCenter visible={showError}>
        <div className="modal-fade" role="dialog">
          <div className="modal-dialog modal-sm alert">
            <div className="modal-content modal-layout-sign-pop-up alert-popup">
              <div className="footer-modal-header">
                <h4 className="footer-modal-title">Alert</h4>
              </div>
              <div className="footer-modal-body">
                <p>Your Order was not signed. Please Try Again.</p>
                <div className="modal-footer removegrey">
                  <button type="button" className="btn btn-default overrideDTK" onClick={closeFailedLayer}>OK</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* Error DTK Non Orders */}
      <ModalContentCenter visible={errorDTKNonOrders}>
        <div className="modal-fade DTKfailed" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content DTKerror alert-popup">
              <div className="footer-modal-header">
                <button type="button" className="btn btn-default close" onClick={closeOverridenPopup}>&times;</button>
                <h4 className="footer-modal-title">Alert</h4>
              </div>
              <div className="footer-modal-body">
                <p className="center">"The order is already in process. Please verify."</p>
              </div>
              <div className="modal-footer removegrey">
                <button type="button" className="btn btn-default overrideDTK" onClick={closeOverridenPopup}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* Error DTK Reconfirm */}
      <ModalContentCenter visible={showErrorDTK}>
        <div className="modal-fade DTKfailed" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content DTKerror alert-popup">
              <div className="footer-modal-header">
                <button type="button" className="btn btn-default close" onClick={refreshCurrentOrder}>&times;</button>
                <h4 className="footer-modal-title">Alert</h4>
              </div>
              <div className="footer-modal-body">
                <p className="center">Please reconfirm details and resubmit order.</p>
              </div>
              <div className="modal-footer removegrey">
                <button type="button" className="btn btn-default overrideDTK" onClick={refreshCurrentOrder}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>
    </>
  );
};

export default SignApprovedOrder;
